import { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

/**
 * 银河星云背景 — 修复版
 * 提高星星可见度：更大粒子 + 更亮 + 发光拖尾
 */

// ──────────────────────────────────────────────
// 顶点 shader
// ──────────────────────────────────────────────
const vertexShader = `
  attribute float aSize;
  attribute vec3 aColor;
  attribute float aPhase;
  uniform float uTime;
  
  varying vec3 vColor;
  varying float vTwinkle;
  
  void main() {
    vColor = aColor;
    
    vec3 pos = position;
    // 缓慢漂浮
    pos.x += sin(uTime * 0.04 + aPhase * 6.28) * 0.15;
    pos.y += cos(uTime * 0.03 + aPhase * 6.28 + 1.5) * 0.12;
    
    // Twinkle
    vTwinkle = sin(uTime * 1.5 + aPhase * 6.28) * 0.4 + 0.6;
    
    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    gl_PointSize = aSize * (250.0 / -mvPosition.z);
    gl_PointSize *= (0.7 + vTwinkle * 0.5);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

// ──────────────────────────────────────────────
// 星星 fragment
// ──────────────────────────────────────────────
const starFragment = `
  varying vec3 vColor;
  varying float vTwinkle;
  
  void main() {
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    
    // 柔光晕
    float alpha = pow(1.0 - smoothstep(0.0, 0.5, d), 1.8);
    alpha *= vTwinkle * 0.85 + 0.15;
    
    gl_FragColor = vec4(vColor, alpha);
  }
`;

// ──────────────────────────────────────────────
// 星云 fragment
// ──────────────────────────────────────────────
const nebulaFragment = `
  varying vec3 vColor;
  
  void main() {
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    float alpha = pow(1.0 - smoothstep(0.0, 0.5, d), 5.0);
    gl_FragColor = vec4(vColor, alpha * 0.15);
  }
`;

// ──────────────────────────────────────────────
// 粒子层
// ──────────────────────────────────────────────
function ParticleLayer({
  count = 600,
  spread = 80,
  sizeRange = [1.5, 4.0],  // 修复：更大的星星
  colors = null,
  opacity = 1.0,
}) {
  const meshRef = useRef();

  const [positions, sizes, phases] = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const sz = new Float32Array(count);
    const ph = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = spread * (0.4 + Math.random() * 0.6);

      pos[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.5;
      pos[i * 3 + 2] = r * Math.cos(phi) * 0.6 - 18;

      sz[i] = sizeRange[0] + Math.random() * (sizeRange[1] - sizeRange[0]);
      ph[i] = Math.random();
    }
    return [pos, sz, ph];
  }, [count, spread, sizeRange[0], sizeRange[1]]);

  // 颜色 — 白/蓝/青/少量金
  const colorArray = useMemo(() => {
    const col = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const hue = Math.random();
      let r, g, b;
      if (hue < 0.5) { r=1; g=1; b=1; }                    // 50% 白
      else if (hue < 0.75) { r=0.65; g=0.82; b=1.0; }      // 25% 蓝
      else if (hue < 0.92) { r=0.6; g=0.95; b=0.9; }       // 17% 青
      else { r=1.0; g=0.88; b=0.6; }                       // 8% 金
      col[i*3] = r; col[i*3+1] = g; col[i*3+2] = b;
    }
    return col;
  }, [count]);

  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
  }));

  useFrame((_, delta) => {
    if (meshRef.current?.material?.uniforms?.uTime) {
      meshRef.current.material.uniforms.uTime.value += delta;
    }
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * 0.002;
    }
  });

  return (
    <points ref={meshRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-aSize" count={count} array={sizes} itemSize={1} />
        <bufferAttribute attach="attributes-aColor" count={count} array={colorArray} itemSize={3} />
        <bufferAttribute attach="attributes-aPhase" count={count} array={phases} itemSize={1} />
      </bufferGeometry>
      <shaderMaterial
        vertexShader={vertexShader}
        fragmentShader={starFragment}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

// ──────────────────────────────────────────────
// 星云团
// ──────────────────────────────────────────────
function NebulaCloud({ color = '#7eccff', count = 25, spread = 35 }) {
  const meshRef = useRef();

  const [positions, sizes, phases, colorArr] = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const sz = new Float32Array(count);
    const ph = new Float32Array(count);
    const col = new Float32Array(count * 3);
    const c = new THREE.Color(color);

    for (let i = 0; i < count; i++) {
      pos[i*3]   = (Math.random() - 0.5) * spread * 2;
      pos[i*3+1] = (Math.random() - 0.5) * spread * 0.6;
      pos[i*3+2] = (Math.random() - 0.5) * spread - 22;
      sz[i] = 8 + Math.random() * 18;
      ph[i] = Math.random();
      col[i*3] = c.r; col[i*3+1] = c.g; col[i*3+2] = c.b;
    }
    return [pos, sz, ph, col];
  }, [count, spread, color]);

  const uniforms = useMemo(() => ({ uTime: { value: 0 } }));

  useFrame((_, delta) => {
    if (meshRef.current?.material?.uniforms?.uTime) {
      meshRef.current.material.uniforms.uTime.value += delta;
    }
  });

  return (
    <points ref={meshRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-aSize" count={count} array={sizes} itemSize={1} />
        <bufferAttribute attach="attributes-aColor" count={count} array={colorArr} itemSize={3} />
        <bufferAttribute attach="attributes-aPhase" count={count} array={phases} itemSize={1} />
      </bufferGeometry>
      <shaderMaterial
        vertexShader={vertexShader}
        fragmentShader={nebulaFragment}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

// ──────────────────────────────────────────────
// 场景组合
// ──────────────────────────────────────────────
function GalaxyScene() {
  return (
    <>
      <ParticleLayer count={600} spread={90} sizeRange={[1.0, 3.5]} />
      <NebulaCloud color="#7eccff" count={18} spread={45} />
      <NebulaCloud color="#ffd857" count={8} spread={30} />
      <NebulaCloud color="#c4a7ff" count={6} spread={25} />
    </>
  );
}

// ──────────────────────────────────────────────
// 导出
// ──────────────────────────────────────────────
export default function StarField({ className, style }) {
  return (
    <div
      className={className}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        ...style,
      }}
    >
      <Canvas
        camera={{ position: [0, 0, 25], fov: 60, near: 0.1, far: 200 }}
        dpr={[1, 1.5]}
        gl={{ antialias: false, alpha: true, powerPreference: 'high-performance' }}
        style={{ background: 'transparent' }}
      >
        <GalaxyScene />
      </Canvas>
    </div>
  );
}
